import axios, { AxiosResponse } from "axios";
import { Feature } from "ol";
import { EsriJSON } from "ol/format";
import { Geometry } from "ol/geom";
import { ISummaryParams } from "../Popups/Registration/SummarySection";
import { config, defaultOption, objectIdGap, proxyUrl } from "./config";
import { AttachmentType, PrintType } from "./enums";

export interface IFieldInfos {
    [layerId: string]: IEsriFieldInfo[];
}
export interface IEsriFieldInfo {
    alias: string;
    domain: IEsriDomain;
    editable: boolean;
    name: string;
    nullable: boolean;
    type: string;
}

export interface IEsriDomain {
    type: string;
    name: string;
    description: string;
    codedValues: IEsriCodedValue[];
    mergePolicy: string;
}

export interface IEsriCodedValue {
    code: string | number;
    name: string;
}

export interface IObjectIDs {
    objectId: number;
    globalId: string;
}

export interface IAttachmentPath {
    type: AttachmentType;
    url: string;
}
export interface IAttachmentFile {
    type: AttachmentType;
    endpoint: string;
    resourceId: string;
}

const esrijsonFormat = new EsriJSON();

const fieldInfoCache: Record<string, IEsriFieldInfo[]> = {};
const featuresCache: Record<number, Feature<Geometry>[]> = {};

export const fetchAllFeatures = async (tab: number): Promise<Feature<Geometry>[]> => {
    if (tab in featuresCache) {
        return Promise.resolve(featuresCache[tab]);
    }
    await fetchFieldInfos();

    featuresCache[tab] = [].concat(...(await Promise.all([fetchFeatures(tab, "0")])));
    return featuresCache[tab];
};

const fetchFeatures = (tab: number, layer: string) => {
    const params = {
        f: "json",
        returnGeometry: true,
        where: "STATE=" + tab,
        outFields: "*",
        outSR: "3059"
    };
    return axios
        .get(proxyUrl + "proxy/A05F8B5E0EB84CAEB9499496474E3093/FeatureServer/" + layer + "/query", {
            params
        })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            const features = esrijsonFormat.readFeatures(response.data);
            fixData(features, layer);

            return features;
        })
        .catch((err) => {
            console.error(err);
            return [];
        });
};

const fetchFieldInfos = async () => await Promise.all([fetchFieldInfo("0"), fetchFieldInfo("1")]);

const fetchFieldInfo = (layerId: string): Promise<IEsriFieldInfo[]> => {
    const params = {
        f: "json",
        returnGeometry: true,
        where: "1=1",
        outFields: "*",
        outSR: "3059"
    };
    return axios
        .get(proxyUrl + "proxy/A05F8B5E0EB84CAEB9499496474E3093/FeatureServer/" + layerId, {
            params
        })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            const info = response.data.fields;
            addDefaultCodedValuesOption(info);
            fieldInfoCache[layerId] = info;
            return info;
        })
        .catch((err) => {
            console.error(err);
            return [];
        });
};

export const getFieldInfos = (): IFieldInfos => ({ "0": fieldInfoCache["0"], "1": fieldInfoCache["1"] });

const addDefaultCodedValuesOption = (info: IEsriFieldInfo[]) => {
    config.sideView.filters.map((filter) => {
        const currentFieldInfo = info.find((item) => item.name === filter.field);
        if (currentFieldInfo && currentFieldInfo.domain && currentFieldInfo.domain.codedValues) {
            currentFieldInfo.domain.codedValues.unshift(defaultOption);
        }
    });
};

const fixData = (features: Feature[], layer: string) => {
    const decimalFields = getDecimalFields(layer);
    const currencyFields = config.dataView.currencyFields;

    for (const feature of features) {
        const properties = {
            VISIBLE: true,
            LAYERID: layer
        };
        if (Number(layer) > 0) {
            properties["OBJECTID"] = feature.get("OBJECTID") + Number(layer) * objectIdGap;
            properties["LAYERID"] = layer;
        }

        for (const fieldName of decimalFields) {
            const value = feature.get(fieldName);
            if (value !== null) {
                properties[fieldName] = parseFloat((value as number).toFixed(8));
            }
        }
        for (const fieldName of currencyFields) {
            const value = feature.get(fieldName);
            if (value !== null) {
                properties[fieldName] = parseFloat(value).toFixed(2) + " " + String.fromCharCode(8364);
            }
        }
        feature.setProperties(properties);
    }
};

const getDecimalFields = (layerId: string): string[] => {
    return fieldInfoCache[layerId]
        .filter((f) => f.type === "esriFieldTypeDouble" || f.type === "esriFieldTypeSingle")
        .map((f) => f.name);
};

export const updateFeatures = async (features: Feature[], layerId?: string) => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }

    const queryString = new URLSearchParams();
    queryString.append("f", "json");
    queryString.append("g-recaptcha-response", recaptchaToken);
    queryString.append(
        "features",
        "[" + features.map((feature) => esrijsonFormat.writeFeature(feature)).toString() + "]"
    );

    return axios
        .post(
            proxyUrl +
                "proxy/recaptcha-a413703ce9074cb4be4597d6ca0d2f12/FeatureServer/" +
                (layerId || 0) +
                "/updateFeatures",
            queryString
        )
        .then((response) => {
            return response;
        })
        .catch((err) => {
            console.error(err);
            return { status: 0 } as AxiosResponse;
        });
};

export const requestObjectIds = async (features: Feature[], layerId?: string): Promise<IObjectIDs[]> => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }

    const queryString = new URLSearchParams();
    queryString.append("f", "json");
    queryString.append("g-recaptcha-response", recaptchaToken);
    queryString.append(
        "features",
        "[" + features.map((feature) => esrijsonFormat.writeFeature(feature)).toString() + "]"
    );

    return axios
        .post(
            proxyUrl +
                "proxy/recaptcha-a413703ce9074cb4be4597d6ca0d2f12/FeatureServer/" +
                (layerId || 0) +
                "/addFeatures",
            queryString
        )
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            const ids: IObjectIDs[] = response.data.addResults.map((res) => ({
                objectId: res.objectId,
                globalId: res.globalId
            }));
            return ids;
        })
        .catch((err) => {
            console.error(err);
            return undefined;
        });
};

export const uploadFile = async (file: File, id: string): Promise<AxiosResponse> => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("RelatedObjectId", id);
    formData.append("FieldName", "FILEID");
    formData.append("LayerName", "PUBSDE.LVMFormNILandPlotFiles");
    formData.append("g-recaptcha-response", recaptchaToken);

    return axios
        .post(config.upload.url, formData, {
            headers: { "Content-Type": "multipart/form-data" }
        })
        .then((response) => {
            return response;
        })
        .catch((err) => {
            console.error(err);
            return { status: 0 } as AxiosResponse;
        });
};

export const printPdf = async (data: ISummaryParams, printType: PrintType): Promise<AxiosResponse> => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }

    const formData = new FormData();
    formData.append("g-recaptcha-response", recaptchaToken);
    formData.append("data", JSON.stringify(data));
    printType === PrintType.Path && formData.append("f", "json");

    return axios
        .post(
            config.print.url + config.print.endpoint,
            formData,
            printType === PrintType.File && {
                responseType: "blob",
                headers: {
                    Accept: "application/pdf"
                }
            }
        )
        .then((response) => {
            return response;
        })
        .catch((err) => {
            console.error(err);
            return { status: 0 } as AxiosResponse;
        });
};

export const sendEmail = async (
    to: string,
    attachments: IAttachmentPath[] | IAttachmentFile[]
): Promise<AxiosResponse> => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }

    const formData = new FormData();
    formData.append("To", to);
    formData.append("Subject", config.email.subject);
    formData.append("Body", config.email.body);
    formData.append("g-recaptcha-response", recaptchaToken);

    if (attachments && attachments.length) {
        attachments.map((a, i) => {
            a.type && formData.append("ExternalAttachments[" + i + "].type", a.type);
            a.url && formData.append("ExternalAttachments[" + i + "].url", a.url);
            a.endpoint && formData.append("ExternalAttachments[" + i + "].endpoint", a.endpoint);
            a.resourceId && formData.append("ExternalAttachments[" + i + "].resourceId", a.resourceId);
        });
    }

    return axios
        .post(config.email.url, formData, {
            headers: { "Content-Type": "multipart/form-data" }
        })
        .then((response) => {
            return response;
        })
        .catch((err) => {
            console.error(err);
            return { status: 0 } as AxiosResponse;
        });
};

const getCaptchaToken = (): Promise<string> => {
    return new Promise((resolve) => {
        //@ts-ignore
        window.grecaptcha.ready(() => {
            //@ts-ignore
            window.grecaptcha
                .execute(config.reCaptchaSiteKey, { action: "submit" })
                .then((token) => resolve(token))
                .catch(() => resolve(null));
        });
    });
};
